import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans text-[13px] font-medium tracking-[0.5px] uppercase transition-ui focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0274b6] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-white hover:bg-[#1a2c2a] active:bg-[#162220] border border-primary",
        destructive: "bg-[#e10000] text-white hover:bg-[#c00000] border border-[#e10000]",
        outline: "border border-[#111111] bg-transparent text-[#111111] hover:bg-[#f4f4f4]",
        secondary: "border border-[#cccccc] bg-transparent text-[#222222] hover:bg-[#F1F1F1]",
        ghost: "bg-transparent text-[#222222] hover:bg-[#f4f4f4] hover:text-[#111111]",
        link: "text-[#0274b6] underline-offset-4 hover:underline hover:text-[#027ebe] tracking-normal normal-case",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 px-4 text-[12px]",
        lg: "h-12 px-7 text-[14px]",
        icon: "h-11 w-11 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
